import type { Bcp47Locale } from '@backmarket/core-types/Locale'
import { isBrowser } from '@backmarket/utils/env/isBrowser'
import {
  type DBSchema,
  type IDBPDatabase,
  type IDBPObjectStore,
  openDB,
} from 'idb'

import {
  DAY_IN_MS,
  MAX_ENTRIES_PER_KIND,
  SCHEMA_VERSION,
  TTL_DAYS,
} from '../constants'
import type {
  BrowsingHistoryEntry,
  BrowsingHistoryKind,
  ListEntriesOptions,
} from '../types'

import { sortByLastVisitedAt } from './recency'

interface BrowsingHistorySchema extends DBSchema {
  entries: { key: string; value: BrowsingHistoryEntry }
}

const STORAGE_NAME = 'browsing-history'
const ENTRIES_STORE = 'entries'

let dbPromise: Promise<IDBPDatabase<BrowsingHistorySchema>> | null = null

/**
 * Opens the database. To evolve the schema: bump SCHEMA_VERSION and extend the
 * `upgrade` callback, e.g. for a v2 adding an index on a new field:
 *
 * upgrade(db, oldVersion, _newVersion, transaction) {
 *   if (oldVersion < 1) db.createObjectStore(ENTRIES_STORE, { keyPath: 'id' })
 *   if (oldVersion < 2) {
 *     transaction.objectStore(ENTRIES_STORE).createIndex('by-slug', 'slug')
 *   }
 * }
 *
 * IndexedDB replays the upgrade transaction on any store that has not reached
 * the new version, so every historical step stays guarded by `oldVersion < N`.
 */
function getHistoryDb() {
  if (!dbPromise) {
    dbPromise = openDB<BrowsingHistorySchema>(STORAGE_NAME, SCHEMA_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(ENTRIES_STORE)) {
          db.createObjectStore(ENTRIES_STORE, { keyPath: 'id' })
        }
      },
    })

    dbPromise.catch((error) => {
      // eslint-disable-next-line no-console
      console.error('[browsing-history] openDB failed', error)
      dbPromise = null
    })
  }

  return dbPromise
}

function isValidEntry(record: unknown): record is BrowsingHistoryEntry {
  if (typeof record !== 'object' || record === null) return false

  const entry = record as Partial<{
    id: unknown
    kind: unknown
    locale: unknown
    lastVisitedAt: unknown
    term: unknown
    landingId: unknown
    productId: unknown
    grade: unknown
    offerType: unknown
  }>

  if (typeof entry.id !== 'string') return false
  if (typeof entry.locale !== 'string') return false
  if (
    typeof entry.lastVisitedAt !== 'number' ||
    !Number.isFinite(entry.lastVisitedAt)
  )
    return false

  if (entry.kind === 'search') return typeof entry.term === 'string'
  if (entry.kind === 'plp') return typeof entry.landingId === 'string'
  if (entry.kind === 'pdp') {
    return (
      typeof entry.productId === 'string' &&
      typeof entry.grade === 'string' &&
      typeof entry.offerType === 'string'
    )
  }

  return false
}

function isExpired(entry: BrowsingHistoryEntry, now: number): boolean {
  return entry.lastVisitedAt < now - TTL_DAYS * DAY_IN_MS
}

function groupByKind(entries: BrowsingHistoryEntry[]) {
  return entries.reduce<Record<BrowsingHistoryKind, BrowsingHistoryEntry[]>>(
    (groups, entry) => {
      groups[entry.kind].push(entry)

      return groups
    },
    { search: [], plp: [], pdp: [] },
  )
}

async function enforceRetention(
  store: IDBPObjectStore<
    BrowsingHistorySchema,
    ['entries'],
    'entries',
    'readwrite'
  >,
  now: number,
) {
  const records = await store.getAll()
  const live = sortByLastVisitedAt(
    records.filter((record) => isValidEntry(record) && !isExpired(record, now)),
  )
  const grouped = groupByKind(live)
  const keptIds = new Set(
    Object.values(grouped).flatMap((entries) =>
      entries.slice(0, MAX_ENTRIES_PER_KIND).map((entry) => entry.id),
    ),
  )

  await Promise.all(
    records
      .filter((record) => !keptIds.has(record.id))
      .map((record) => store.delete(record.id)),
  )
}

export async function upsertVisit(entry: BrowsingHistoryEntry): Promise<void> {
  if (!isBrowser()) return

  try {
    const db = await getHistoryDb()
    const transaction = db.transaction(ENTRIES_STORE, 'readwrite')

    await transaction.store.put(entry)
    await enforceRetention(transaction.store, Date.now())
    await transaction.done
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[browsing-history] upsertVisit failed', error)
  }
}

export async function listEntries(
  locale: Bcp47Locale,
  now: number,
  options?: ListEntriesOptions,
): Promise<BrowsingHistoryEntry[]> {
  if (!isBrowser()) return []

  try {
    const db = await getHistoryDb()
    const records = await db.getAll(ENTRIES_STORE)

    const entries = sortByLastVisitedAt(
      records.filter(
        (record) =>
          isValidEntry(record) &&
          record.locale === locale &&
          !isExpired(record, now),
      ),
    )

    const kindFilter = options?.kind
    let kinds: BrowsingHistoryKind[] | undefined

    if (kindFilter) {
      kinds = Array.isArray(kindFilter) ? kindFilter : [kindFilter]
    }

    const termFilter = options?.term

    const filtered = entries.filter((entry) => {
      if (kinds && !kinds.includes(entry.kind)) return false

      if (termFilter !== undefined) {
        if (entry.kind !== 'search') return false

        if (!entry.term.toLowerCase().includes(termFilter.toLowerCase()))
          return false
      }

      return true
    })

    const limit = options?.limit

    return limit === undefined
      ? filtered
      : filtered.slice(0, Math.max(0, limit))
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[browsing-history] listEntries failed', error)

    return []
  }
}

export async function clearEntries(): Promise<void> {
  if (!isBrowser()) return

  try {
    const db = await getHistoryDb()

    await db.clear(ENTRIES_STORE)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[browsing-history] clearEntries failed', error)
  }
}
